import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Anchor, Box, Button, Divider, Group, Stack, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import HeaderShell, { drawerItemClass, navLinkClass } from '@/components/header/HeaderShell';
import { useCloseOnDesktop } from '@/hooks/useCloseOnDesktop';
import { useScrollSpy } from '@/hooks/useScrollSpy';
import { scrollBehavior } from '@/lib/motion';
import { ROUTES } from '@/routes/paths';
import { useAuth } from '@/stores/authContext';

interface SectionAnchor {
  readonly id: string;
  readonly key: string;
}

// Якоря секций лендинга (id совпадают с id секций: how/trade/certification/forwarding).
const ANCHORS: readonly SectionAnchor[] = [
  { id: 'how', key: 'nav.anchors.how' },
  { id: 'trade', key: 'nav.anchors.trade' },
  { id: 'certification', key: 'nav.anchors.certification' },
  { id: 'forwarding', key: 'nav.anchors.forwarding' },
];

// Стабильная ссылка для зависимости эффекта scroll-spy (иначе новый массив каждый рендер).
const ANCHOR_IDS = ANCHORS.map((anchor) => anchor.id);

export default function PublicHeader() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [opened, { toggle, close }] = useDisclosure(false);
  const navigate = useNavigate();
  const location = useLocation();
  // На главной активный пункт меню следует за прокруткой (scroll-spy); на других страницах — выкл.
  useCloseOnDesktop(close);
  const activeSection = useScrollSpy(ANCHOR_IDS, location.pathname === ROUTES.HOME);
  const aboutActive = location.pathname === ROUTES.ABOUT;

  function scrollToSection(id: string): void {
    close();
    if (location.pathname === ROUTES.HOME) {
      document.getElementById(id)?.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    } else {
      navigate(ROUTES.HOME);
      // дождаться рендера лендинга, затем проскроллить к якорю
      window.setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      }, 120);
    }
  }

  // Заголовок мобильного меню «Дата 1861» работает как клик по лого: на главной — скролл наверх,
  // иначе переход на главную и наверх. Дополнительно закрывает меню.
  function goHome(): void {
    close();
    if (location.pathname === ROUTES.HOME) {
      window.scrollTo({ top: 0, behavior: scrollBehavior() });
    } else {
      navigate(ROUTES.HOME);
      window.setTimeout(() => window.scrollTo({ top: 0 }), 0);
    }
  }

  // Пункты-якоря: и в шапке, и в мобильном меню. Текущая секция отмечена aria-current для экранных чтецов.
  const renderAnchors = (variant: 'desktop' | 'mobile') =>
    ANCHORS.map(({ id, key }) => {
      const active = activeSection === id;
      return (
        <Anchor
          key={id}
          component="button"
          type="button"
          onClick={() => scrollToSection(id)}
          className={variant === 'desktop' ? navLinkClass(active) : drawerItemClass(active)}
          aria-current={active ? 'true' : undefined}
          underline="never"
        >
          {t(key)}
        </Anchor>
      );
    });

  const actions = (
    <>
      <Group h="100%" gap="md" visibleFrom="md" wrap="nowrap">
        <Anchor
          component={Link}
          to={ROUTES.ABOUT}
          className={navLinkClass(aboutActive)}
          aria-current={aboutActive ? 'page' : undefined}
          underline="never"
        >
          {t('nav.about')}
        </Anchor>
        {isAuthenticated ? (
          <Button component={Link} to={ROUTES.APP} size="sm">
            {t('notFound.toCabinet')}
          </Button>
        ) : (
          <>
            <Button component={Link} to={ROUTES.LOGIN} variant="default" size="sm">
              {t('nav.login')}
            </Button>
            <Button component={Link} to={ROUTES.REGISTER} size="sm">
              {t('nav.register')}
            </Button>
          </>
        )}
      </Group>
      <Box hiddenFrom="md">
        <Button component={Link} to={isAuthenticated ? ROUTES.APP : ROUTES.REGISTER} size="compact-sm">
          {t(isAuthenticated ? 'notFound.toCabinet' : 'nav.register')}
        </Button>
      </Box>
    </>
  );

  const menu = (
    <>
      <Stack component="nav" gap={4} aria-label={t('nav.mainLabel')}>
        {renderAnchors('mobile')}
        <Anchor
          component={Link}
          to={ROUTES.ABOUT}
          onClick={close}
          className={drawerItemClass(aboutActive)}
          aria-current={aboutActive ? 'page' : undefined}
          underline="never"
        >
          {t('nav.about')}
        </Anchor>
      </Stack>
      <Divider my="xs" />
      {isAuthenticated ? (
        <Button component={Link} to={ROUTES.APP} onClick={close} fullWidth size="sm">
          {t('notFound.toCabinet')}
        </Button>
      ) : (
        <>
          <Button component={Link} to={ROUTES.LOGIN} variant="default" onClick={close} fullWidth size="sm">
            {t('nav.login')}
          </Button>
          <Button component={Link} to={ROUTES.REGISTER} onClick={close} fullWidth size="sm">
            {t('nav.register')}
          </Button>
        </>
      )}
    </>
  );

  return (
    <HeaderShell
      nav={renderAnchors('desktop')}
      actions={actions}
      menuOpened={opened}
      onMenuToggle={toggle}
      onMenuClose={close}
      menuTitle={
        <UnstyledButton onClick={goHome} fw={600} fz="md" c="var(--mantine-color-text)">
          {t('app.name')}
        </UnstyledButton>
      }
      menu={menu}
    />
  );
}
